import { useRef, useEffect } from "react";
import { normalizeEmotion } from "../../utils/emotion";
import { CONTENT_H, CONTENT_W, fillCircle, fillEllipse, fillRect, hash01 } from "./chunk-0.jsx";
export function RawEyes({
  emotion,
  poke,
  heartbeatRef,
  accent,
  active = true,
  motionBoost = false
}) {
  const canvasRef = useRef(null);
  const lastPaintRef = useRef(0);
  const pokeAtRef = useRef(0);
  useEffect(() => {
    pokeAtRef.current = Date.now();
  }, [poke]);
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(CONTENT_W * dpr);
    canvas.height = Math.floor(CONTENT_H * dpr);
    canvas.style.width = `${CONTENT_W}px`;
    canvas.style.height = `${CONTENT_H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // approximate firmware default palette (warm amber)
    const BG = '#000000';
    const sclera = '#e8e8e8';
    const iris = accent || '#ffaa28';
    const pupil = '#050505';
    const scleraOfficial = '#fafafa';
    const pupilOfficial = '#000000';
    const highlight = '#ffffff';
    const outline = '#121212';
    const eyeStyle = 0; // 0: modern (colored iris), 1: official (outline)
    const seed = hash01(`raw:${emotion || 'neutral'}`);
    const draw = nowMs => {
      if (lastPaintRef.current && nowMs - lastPaintRef.current < 40) return; // ~25fps
      lastPaintRef.current = nowMs;
      if (heartbeatRef) heartbeatRef.current = nowMs;
      ctx.clearRect(0, 0, CONTENT_W, CONTENT_H);
      const w = CONTENT_W;
      const h = CONTENT_H;
      const eyeCy = Math.floor(h / 2);
      const shortSide = Math.min(w, h);
      const eyeRx = Math.max(30, Math.floor(shortSide * (eyeStyle === 1 ? 26 : 22) / 100));
      const eyeRyBase = Math.max(24, Math.floor(shortSide * (eyeStyle === 1 ? 22 : 18) / 100));
      const eyeOffsetX = Math.max(eyeRx + 10, Math.floor(w / 4));
      const leftCx = Math.floor(w / 2) - eyeOffsetX;
      const rightCx = Math.floor(w / 2) + eyeOffsetX;

      // blink envelope (same as firmware)
      let blink = 0;
      const pokeAge = pokeAtRef.current ? nowMs - pokeAtRef.current : 1e9;
      const forced = pokeAge >= 0 && pokeAge < 450;
      const t = (forced ? 3150 : nowMs) % 3300;
      if (t > 3000) {
        const phase = (t - 3000) / 300;
        blink = Math.sin(Math.min(phase, 1) * Math.PI);
      }
      let squint = 0;
      let gazeDx = 0;
      let gazeDy = 0;
      let pupilR = Math.max(9, eyeStyle === 1 ? Math.floor(eyeRyBase * 55 / 100) : Math.floor(eyeRyBase / 2));
      let irisR = Math.max(14, pupilR + 10);
      const emo = normalizeEmotion(emotion);
      if (emo === 'happy') {
        squint = 0.3;
        gazeDy = 0.25;
      } else if (emo === 'angry') {
        squint = 0.18;
        gazeDx = -0.35;
        pupilR = Math.max(7, pupilR - 2);
      } else if (emo === 'sleepy') {
        blink = Math.max(blink, 0.65);
        squint = Math.max(squint, 0.25);
        pupilR = Math.max(6, pupilR - 4);
        gazeDy = 0.45;
      } else if (emo === 'surprised') {
        pupilR = Math.min(eyeRyBase, pupilR + 6);
        irisR = Math.min(eyeRyBase, irisR + 4);
      }
      const tt = nowMs * 0.001;
      const baseK = emo === 'neutral' || emo === 'idle' ? motionBoost ? 1.0 : 0.8 : motionBoost ? 0.55 : 0.25;
      const k = (eyeStyle === 1 ? 0.55 : 1.0) * baseK;
      gazeDx += k * (0.22 * Math.sin(tt * 0.9 + seed * 10) + 0.12 * Math.sin(tt * 2.3 + seed * 22));
      gazeDy += k * (0.18 * Math.sin(tt * 1.2 + seed * 16 + 1.3) + 0.08 * Math.sin(tt * 2.8 + seed * 7));
      const eyeRyF = Math.max(6, eyeRyBase * (1.0 - 0.85 * blink - squint));
      const eyeRy = Math.floor(eyeRyF);
      const maxDx = Math.max(0, eyeRx - irisR - 4);
      const maxDy = Math.max(0, eyeRy - irisR - 4);
      const irisDx = Math.round(Math.max(-1, Math.min(1, gazeDx)) * maxDx);
      const irisDy = Math.round(Math.max(-1, Math.min(1, gazeDy)) * maxDy);
      if (eyeStyle === 1) {
        fillEllipse(ctx, leftCx, eyeCy, eyeRx + 2, eyeRy + 2, outline);
        fillEllipse(ctx, rightCx, eyeCy, eyeRx + 2, eyeRy + 2, outline);
        fillEllipse(ctx, leftCx, eyeCy, eyeRx, eyeRy, scleraOfficial);
        fillEllipse(ctx, rightCx, eyeCy, eyeRx, eyeRy, scleraOfficial);
        fillCircle(ctx, leftCx + irisDx, eyeCy + irisDy, pupilR, pupilOfficial);
        fillCircle(ctx, rightCx + irisDx, eyeCy + irisDy, pupilR, pupilOfficial);
        fillCircle(ctx, leftCx + irisDx - Math.floor(pupilR / 3), eyeCy + irisDy - Math.floor(pupilR / 3), Math.max(2, Math.floor(pupilR / 6)), highlight);
        fillCircle(ctx, rightCx + irisDx - Math.floor(pupilR / 3), eyeCy + irisDy - Math.floor(pupilR / 3), Math.max(2, Math.floor(pupilR / 6)), highlight);
      } else {
        fillEllipse(ctx, leftCx, eyeCy, eyeRx, eyeRy, sclera);
        fillEllipse(ctx, rightCx, eyeCy, eyeRx, eyeRy, sclera);
        fillCircle(ctx, leftCx + irisDx, eyeCy + irisDy, irisR, iris);
        fillCircle(ctx, rightCx + irisDx, eyeCy + irisDy, irisR, iris);
        fillCircle(ctx, leftCx + irisDx, eyeCy + irisDy, pupilR, pupil);
        fillCircle(ctx, rightCx + irisDx, eyeCy + irisDy, pupilR, pupil);
      }
      if (blink > 0.01 || squint > 0.01) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        const lidH = Math.round(eyeRyBase * (blink + squint) * 1.1);
        const yTop = eyeCy - eyeRyBase - 2;
        const yBot = eyeCy + eyeRyBase + 2;
        fillRect(ctx, leftCx - eyeRx - 2, yTop, leftCx + eyeRx + 2, yTop + lidH, BG);
        fillRect(ctx, rightCx - eyeRx - 2, yTop, rightCx + eyeRx + 2, yTop + lidH, BG);
        if (blink > 0.55) {
          const lidH2 = Math.round(eyeRyBase * (blink - 0.5) * 1.6);
          fillRect(ctx, leftCx - eyeRx - 2, yBot - lidH2, leftCx + eyeRx + 2, yBot, BG);
          fillRect(ctx, rightCx - eyeRx - 2, yBot - lidH2, rightCx + eyeRx + 2, yBot, BG);
        }
        ctx.restore();
      }
    };
    let raf = 0;
    const loop = () => {
      draw(Date.now());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [emotion, accent, active, motionBoost]);
  return <div className="w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} width={CONTENT_W} height={CONTENT_H} className="block" />
    </div>;
}
